import { Component, OnInit, Self, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { DestroyService } from 'src/app/services/destroyservice';
import { takeUntil } from 'rxjs';
import { DefaultValuesService } from '../../../../../services/defaultvalues.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'otasettings-list',
  standalone: false,
  templateUrl: './otasettings-list.component.html',
  providers: [DestroyService]
})
export class OtaSettingsListComponent implements OnInit {

  configForm!: FormGroup;
  dataSource = new MatTableDataSource<any>();
  selectedLevel?: OtaSettingsLevel;
  entries: OtaSettingsViewModel[] = [];
  envEntries: OtaSettingsViewModel[] = [];

  @ViewChild(MatTable, { static: true }) table!: MatTable<any>;
  get columnsToDisplay(): string[] {
    const cols: string[] = [];
    if (this.selectedLevel?.tenantId) {
      cols.push('tenantId');
    }
    cols.push('env', 'mode');
    if (this.selectedLevel?.tenantId) {
      cols.push('envMode');
    }
    cols.push('actions');
    return cols;
  }

  modes: string[] = [
    'Disabled',
    'Inherited',
    'Automatic',
    'DownloadAndWaitForDsc',
    'DownloadAndWaitForNxEMT'
  ];

  private readonly modeLabels: Record<string, string> = {
    DownloadAndWaitForDsc: 'Download and wait for DSC',
    DownloadAndWaitForNxEMT: 'Download and wait for NxEMT'
  };

  getModeLabel(mode: string): string {
    return this.modeLabels[mode] ?? mode;
  }

  get availableModes(): string[] {
    if (this.selectedLevel?.tenantId) {
      return this.modes;
    }
    return this.modes.filter(m => m !== 'Inherited');
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly emtConfigurationService: EquipmentManagenementService,
    public defaults: DefaultValuesService,
    private readonly toastrService: ToastrService,
    @Self() private readonly destroy$: DestroyService
  ) {
    this.configForm = this.fb.group({
      configRows: this.fb.array([])
    });
  }

  ngOnInit() {
    this.defaults.stageMessageChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.selectedLevel) {
        this.loadOtaSettings();
      }
    });
  }
  
  get configRows(): FormArray {
    return this.configForm.get('configRows') as FormArray;
  }

  getFormValue(i: number) {
    return this.configRows.value[i];
  }

  loadOtaSettings() {
    this.emtConfigurationService.getOtaSettings(
        this,
        this.selectedLevel!,
        result => {
            this.entries = result;
            this.setForm(this.entries);
            this.dataSource = new MatTableDataSource((this.configForm.get('configRows') as FormArray).controls);
        },
        error => {
            this.toastrService.error("Unable to load OTA settings");
        }
    );
  }

  loadEnvEntries(): void {
    this.emtConfigurationService.getOtaEnvsSettings(
      this,
      (result: OtaSettingsViewModel[]) => {
        this.envEntries = result;
      },
      (_err: any) => {
        this.toastrService.error("Unable to load env OTA settings");
      }
    );
  }

  getEnvEffectiveMode(environment: string): string {
    const e = this.envEntries.find(x => x.environment?.toLowerCase() === environment?.toLowerCase());
    return e?.mode ?? '';
  }

  setForm(data: OtaSettingsViewModel[]) {
    const rows = data.map(row =>
      this.fb.group({
        Environment: new FormControl(row.environment),
        TenantId: new FormControl(row.tenantId),
        BranchNumber: new FormControl(row.branchNumber),
        DeviceId: new FormControl(row.deviceId),
        Mode: new FormControl(row.mode),
        OnEdit: new FormControl(false)
      })
    );

    this.configForm.setControl('configRows', this.fb.array(rows));

    this.dataSource = new MatTableDataSource(
      (this.configForm.get('configRows') as FormArray).controls
    );
  }

  onRowEditInit(index: number) {
    this.configRows.at(index).get('OnEdit')?.setValue(true);
  }

  onRowEditCancel(index: number) {
    const row = this.configRows.at(index);

    const original = this.entries.find(e =>
      e.tenantId === row.value.TenantId &&
      e.branchNumber === row.value.BranchNumber &&
      e.deviceId === row.value.DeviceId
    );
    if (original) {
      row.patchValue({
        Mode: original.mode
      });
    }

    // exit edit mode
    row.get('OnEdit')?.setValue(false);
  }

  onSave() {
    const changed = this.configRows.value.filter((x: any) => x.OnEdit);
    const updatedEntries: any[] = [];

    changed.forEach((row: any) => {
      const entry = this.entries.find(e =>
        e.tenantId === row.TenantId &&
        e.branchNumber === row.BranchNumber &&
        e.deviceId === row.DeviceId
      );

      if (!entry) return;

      const hasChanges = entry.mode !== row.Mode;

      if (hasChanges) {
        updatedEntries.push({ ...entry, mode: row.Mode });
      }
    });

    this.emtConfigurationService.setOtaSettings(
      this,
      updatedEntries,
      result => {
        this.resetData();
      },
      error => {
        this.toastrService.error("Error while trying to save OTA settings");
      }
    );
  }

  anyChange(): boolean {
    return this.configRows.value.some(x => x.OnEdit);
  }

  changeSelectedLevel(level: EquipmentConfigurationLevel): void{
    this.selectedLevel = level
    this.resetData()
  }

  resetData() {
    this.configForm = this.fb.group({
        configRows: this.fb.array([])
    });
    this.configForm.reset();
    this.dataSource.data = [];

    this.loadOtaSettings();
    if (this.selectedLevel?.tenantId) {
      this.loadEnvEntries();
    }
  }
}